import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useState,
  useMemo
} from 'react'

import { useQuery } from '@tanstack/react-query'
import { FormHandles } from '@unform/core'

import { history } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import { listCheckingAccountService } from '~/modules/retailMedia/services/checking-account'

import { getNewtailMedia } from '../../api'
import { PUBLISHER_ID_QUERY_KEY, contextActions } from '../../constants'
import { bodyRequestAdsDTO } from '../../dtos/bodyRequestAdsDTO'
import { removeUndefined } from '../../helpers'
import { apiURL, apiURLStaging } from '../../services'

const RequestAdsContext = createContext<RequestAdsProviderData>(
  {} as RequestAdsProviderData
)

const RequestAdsProvider = ({ children }: React.PropsWithChildren) => {
  const formRef = useRef<FormHandles>(null)
  const searchParams = useQueryParams()

  const URLparams = Object.fromEntries(searchParams.entries())
  const initialData: FormDataRequestAds = {
    type: (URLparams.type as CampaignAdType) || 'product',
    context: (URLparams.context as CampaignContext) || 'search',
    quantity: Number(URLparams.quantity) || 1,
    size: URLparams.size || '',
    targeting: {
      categories: URLparams.category_name || ''
    },
    product_sku: URLparams.product_sku || '',
    brand_name: URLparams.brand_name || '',
    term: URLparams.term || '',
    brand: URLparams.brand || '',
    session_id: null,
    user_id: null,
    store_id: null,
    channel: 'site',
    dedup_campaign_ads: false,
    dedup_ads: false
  }

  // States
  const [type, setType] = useState<CampaignAdType>(initialData.type)
  const [context, setContext] = useState<CampaignContext>(initialData.context)
  const [loadingAds, setLoadingAds] = useState(false)
  const [errosAds, setErrorAds] = useState<Error | null>(null)
  const [ads, setAds] = useState<ResponseAds>()
  const [curlCommand, setCurlCommand] = useState<string>()

  // Handle publisher id (gambi!)
  const { data: publisherId } = useQuery({
    queryKey: PUBLISHER_ID_QUERY_KEY,
    queryFn: async () => {
      const response = await listCheckingAccountService({ quantity: 1 })

      return response.data.data?.[0]?.publisher_id as PublisherId
    }
  })

  // Handle URL change
  const handleURLChange = useCallback(
    ({ name, value }: HandleURLChangeProps) => {
      const newParams = new URLSearchParams(searchParams.toString())

      if (!value) {
        newParams.delete(name)
        history.push({ search: newParams.toString() })
        return
      }

      if (name === 'type') {
        // Always reset size when ad type changes to avoid carrying invalid values.
        newParams.delete('size')
        formRef.current?.clearField('size')
        newParams.set(name, String(value))
        history.push({ search: newParams.toString() })
        return
      }

      if (name === 'context' && contextActions[value]) {
        contextActions[value].forEach(param => newParams.delete(param))
        newParams.set(name, String(value))
        history.push({ search: newParams.toString() })
        return
      }

      newParams.set(name, String(value))
      history.push({ search: newParams.toString() })
    },
    [searchParams]
  )

  // Handle form change
  const handleChange = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      const target = event.target as HTMLInputElement
      const name = target.name as RequestAdsURLParams
      const value = target.value

      handleURLChange({ name, value })
    },
    [handleURLChange]
  )

  // Handle submit
  const handleSubmit = useCallback(
    (formData: FormDataRequestAds) => {
      if (!publisherId) return

      const payload = removeUndefined(
        bodyRequestAdsDTO(formData)
      ) as PayloadRequestAds
      const isStaging = formData.api_staging

      const buildCurlExample = (payload: PayloadRequestAds) => {
        const baseURL = isStaging ? apiURLStaging : apiURL
        const url = `${baseURL}/${publisherId}`
        const body = JSON.stringify(payload, null, 2)

        return `curl -X POST "${url}" \\
        -H "Content-Type: application/json" \\
        -d '${body}'`
      }

      setCurlCommand(buildCurlExample(payload))

      setLoadingAds(true)
      setErrorAds(null)

      if (formData.debug) {
        payload.debug = true
      }

      getNewtailMedia({
        publisherId,
        body: payload,
        isStaging
      })
        .then(response => {
          const { placementRequestTester } = response.data

          setAds(placementRequestTester)
          setLoadingAds(false)
        })
        .catch(error => {
          setLoadingAds(false)
          setErrorAds(error)
          console.error('Error fetching VTEX Ads:', error)
        })
    },
    [publisherId]
  )

  const showContextSection = useMemo(() => context !== 'home', [context])

  return (
    <RequestAdsContext.Provider
      value={{
        initialData,
        type,
        setType,
        context,
        setContext,
        handleChange,
        handleURLChange,
        handleSubmit,
        formRef,
        loadingAds,
        errosAds,
        ads,
        curlCommand,
        publisherId,
        showContextSection
      }}
    >
      {children}
    </RequestAdsContext.Provider>
  )
}

function useRequestAds(): RequestAdsProviderData {
  const context = useContext(RequestAdsContext)

  if (!context) {
    throw new Error('useRequestAds must be used within a RequestAdsProvider')
  }

  return context
}

export { RequestAdsContext, RequestAdsProvider, useRequestAds }
